# Ontology Factory

<Lede>

A system can retrieve every document containing `customer` and confuse
the purchaser, user, and caller. More documents preserve that ambiguity; they
do not resolve it.

An ontology factory makes the choice explicit: which concepts the product
recognizes, how they relate, what evidence supports a claim, and which actions
that claim permits.

<Callout label="Core thesis" emphasis>

More context is not the same as better context. Better context has a map.

</Callout>

</Lede>

<Section index="01" title="AI Needs a Map, Not a Larger Pile of Documents">

Retrieval finds relevant material but cannot decide which local meaning controls
a decision. *Conversation* may mean a transport session, two humans speaking,
or an interaction spanning transfers. A model can surface all three and build
against the wrong one.

An ontology distinguishes the contract owner from its user, states the evidence
for each role, and preserves uncertainty. Retrieval can then assemble the
relevant map instead of treating every nearby sentence as authoritative. The
goal is selective context, not a larger prompt.

</Section>

<Section index="02" title="A Map Is a Commitment, Not a Mirror">

Here is a practical working definition:

<Quote>

A product ontology states which distinctions the system will recognize, how
they relate, and what evidence is sufficient to make claims about them.

</Quote>

The factory's ontology is not a description of what its repository happens to
look like. A formal <Term definition="An explicit account of the concepts, relationships, and constraints a system recognizes.">ontology</Term> may span schemas, APIs, policies, types,
tests, and prose. The important move is making its commitments reviewable.

A hospital, insurer, and patient may model an encounter differently because
decisions differ. Visible boundaries make them compatible.

An owner decides when evidence changes a state, a distinction fails, or an
action exceeds its authority. Human judgment keeps the map useful; the ontology
makes it testable.

</Section>

<Section index="03" title="The Smallest Useful Ontology Packet">

A useful minimum has six parts:

| Part | Question | Typical expressions |
|---|---|---|
| **Vocabulary** | Which terms do we use, and which apparent synonyms must stay separate? | Definitions, aliases, naming rules, prohibited conflations |
| **Entities and relationships** | What exists in this model, and how can those things connect? | Schemas, graphs, types, identifiers, cardinalities |
| **States and invariants** | What may change, and what must remain true? | State machines, constraints, validation rules, tests |
| **Evidence** | What warrants a claim, and how certain is it? | Events, provenance, observations, confidence, timestamps |
| **Actions and permissions** | What may follow from the claim, who owns the decision, and when must work escalate? | Policies, capabilities, approvals, exception paths |
| **Examples and evaluations** | What counts as an ordinary case, a boundary, a counterexample, or a successful outcome? | Fixtures, scenarios, acceptance tests, observed consequences |

Together they form one semantic contract. Vocabulary without relationships is
a glossary. Entities without evidence turn populated fields into truth.
Evidence without permissions becomes authority. Actions without evaluation
cannot improve. The packet may span prose, graphs, types, policies, and tests,
provided those artifacts point to the same distinctions.

</Section>

<Section index="04" title="Boundaries and Ownership Prevent Semantic Slop">

<Term definition="A boundary within which a particular model and vocabulary remain internally consistent.">Bounded contexts</Term> let teams use one word differently. Terms remain stable
inside each context; translation is explicit at the boundary.

Repository structure can expose those commitments. Consider this path:

<Flow>libs/edge/audio/state-zustand-player</Flow>

In SoundSculpt, each segment carries meaning: reusable owner, product-facing
layer, audio capability, and reactive player-state responsibility. The path is
an ownership claim, not merely an address.

<Asset id="path-ownership-motif" />

A layer can do more than locate the owner. It can select what belongs in that
part of the stack and how the work is built, instrumented, and proved. Edge work
requires product-facing integration, integration tests, and PostHog and Sentry
wrapping. Schema work generates TypeScript interfaces from the database
contract. Engine work pairs deterministic logic with unit tests. Skills and
tool calls apply these rules automatically.

<Asset id="layer-guidance-motif" />

A README defines scope. An AGENTS contract governs work and verification. A
skill supplies a specialized procedure. Their authority remains separate.

For each task, the factory dynamically composes the relevant owners, rules, and
procedures within the agent's context budget. Selection changes what it holds,
not which contract governs.

<Asset id="context-composition-motif" />

A stable identifier finds the owner. Typed relationships constrain
dependencies. Contracts route work. Procedures perform it. Evaluation tests
the original commitment. Without those boundaries, generated work becomes
<Term definition="Polished output built from collapsed concepts, incompatible contexts, or unsupported claims.">semantic slop</Term>: polished and executable, but wrong in meaning.

</Section>

<Section index="05" title="Two Examples Make the Cost Visible">

An ontology matters when confusing concepts changes behavior. Mango must split
events ordinary language compresses. SoundSculpt must preserve relationships a
simple object model would flatten.

### Mango: a protocol answer is not a conversation

“When an answered call ends, send the customer a follow-up” sounds precise, but
*answered* may mean a provider connection, voicemail, human participation, or a
Mango-defined conversation. SIP sessions and successful responses establish
technical facts, not that two humans spoke. Answering-machine detection adds a
fallible classification that may remain unknown.

Mango therefore needs an evidence hierarchy rather than one overloaded `call`
record:

<Flow>call attempt → session or dialog established → machine, human, or unknown classification → sufficient evidence of human participation → Mango-defined conversation</Flow>

Each arrow requires evidence and a product rule. Transport observations say
what happened; product claims say what Mango may conclude. Permissions attach
to the justified state, so ambiguity can delay, escalate, or prevent action.

### SoundSculpt: one creative object is several related things

Music presents the opposite risk: flattening a work, its realizations, and its
reception into one object.

<Asset id="soundsculpt-ontology-map" />

Copyright law distinguishes a musical work from its sound recording. Timbre
research treats perception as multidimensional. SoundSculpt can therefore
attach a timbre assessment to a rendered sound, while perceived mood remains a
relationship among rendering, listener, and context. The ontology locates each
claim where it becomes valid instead of forcing every quality into an intrinsic
field.

### Controlled language helps after the model exists

ASD-STE100 Simplified Technical English constrains vocabulary and usage. A
six-task 2026 experiment reduced mechanical rule violations but did not measure
factual correctness, completeness, or safety. Controlled language clarifies
claims; it cannot decide what `answered` or mood refers to. The factory needs a
sound model and clear expression.

</Section>

<Section index="06" title="The Ontology Learns from Use">

An ontology that never changes is a museum. A working ontology participates in
the same loop as the product:

<Flow>observation → ontology commitment → assembled context and implementation → evaluation → counterexample or consequence → revision</Flow>

New distinctions change schemas, interfaces, tests, prompts, and policies.
Observations expose missing states, weak evidence, or absent authority. Revision
versions terms, identifies owners, migrates dependents, and preserves <Term definition="The record of where a claim or piece of evidence came from and how it changed.">provenance</Term>.

Tests check consistency; review challenges distinctions; consequences test
permitted actions. Together they enable correction.
The factory's product is not a perfect map, but a governed way to revise it.

<ArticleLink slug="the-cognitive-factory">The Cognitive Factory</ArticleLink> uses this map to interpret signals, connect history with current priorities,
and discover the context a decision needs. Shared definitions make that memory
usable across people, agents, and organizational boundaries.

</Section>

<Section index="07" title="Sources">

- Thomas R. Gruber, [“A Translation Approach to Portable Ontology Specifications”](https://tomgruber.org/writing/ontolingua-kaj-1993.pdf) (1993).
- Eric Evans, [*Domain-Driven Design Reference*](https://www.domainlanguage.com/ddd/reference/).
- IETF, [RFC 3261: SIP](https://www.rfc-editor.org/rfc/rfc3261.html).
- Twilio, [Call Resource](https://www.twilio.com/docs/voice/api/call-resource) and [Answering Machine Detection](https://www.twilio.com/docs/voice/answering-machine-detection).
- U.S. Copyright Office, [Circular 56A](https://www.copyright.gov/circs/circ56a.pdf).
- John M. Grey, [“Multidimensional Perceptual Scaling of Musical Timbres”](https://doi.org/10.1121/1.381428) (1977).
- Alf Gabrielsson, “Emotion Perceived and Emotion Felt” (2001); Patrik N. Juslin and Daniel Västfjäll, [“Emotional Responses to Music”](https://doi.org/10.1017/S0140525X08005293) (2008).
- ASD Simplified Technical English Maintenance Group, [ASD-STE100](https://asd-ste100.org), Issue 9 (2025).
- Ege Chelebi, [“The cure for AI slop is a 1986 aircraft manual”](https://www.chele.bi/videos/the-cure-for-ai-slop) (2026).

</Section>
